import { FC } from 'react'
import { alpha, Box, Button, styled } from '@mui/material'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface Props {
  thumbnailHtml: string
  questionNumber: number
  onClick: () => void
}

const VideoThumbnail: FC<Props> = ({ thumbnailHtml, questionNumber, onClick }) => (
  <VideoThumbnailContainer>
    <ThumbnailImageContainer dangerouslySetInnerHTML={{ __html: thumbnailHtml }} />

    <VideoThumbnailOverlay onClick={onClick}>
      <WatchExplanationButton
        disableFocusRipple
        disableRipple
        aria-label={questionNumber ? `Watch Explanation Video for question ${questionNumber}` : 'Watch Explanation Video'}
      >
        <WatchExplanationIcon icon={faPlay} />
        <WatchExplanationText>Watch Explanation</WatchExplanationText>
      </WatchExplanationButton>
    </VideoThumbnailOverlay>
  </VideoThumbnailContainer>
)

const VideoThumbnailContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: '3px',
  border: `1px solid ${theme.palette.grey[100]}`,
}))

const VideoThumbnailOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.grey[50], 0.7),
  transition: theme.transitions.create(['background-color']),
  cursor: 'pointer',

  '&:hover, &:focus-within': {
    backgroundColor: alpha(theme.palette.blue[100], 0.3),

    '& button': {
      backgroundColor: theme.palette.blue[700],
    },
    '& span, & svg': {
      color: theme.palette.common.white,
    },
  },
}))

const ThumbnailImageContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',

  '& img, & a': {
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
  },
}))

const WatchExplanationButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: 'fit-content',
  padding: theme.spacing(4, 3),
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '50px',
  backgroundColor: theme.palette.blue[100],
  transition: theme.transitions.create(['background-color']),
  '&:hover': {
    backgroundColor: theme.palette.blue[700],
  },
}))

const WatchExplanationText = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  lineHeight: '1.0625rem',
  fontWeight: 500,
  color: theme.palette.blue[800],
  margin: theme.spacing(0),
  transition: theme.transitions.create(['color']),
}))

const WatchExplanationIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.blue[800],
  transition: theme.transitions.create(['color']),
}))

export default VideoThumbnail
